import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
const _excluded = ["renderValue", "formatScreenReaderValue", "valueNow", "valueMax", "screenReaderLabel", "size", "color", "meterColor", "renderValueInside", "styles"];
var _dec, _dec2, _class, _ProgressBar;
/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import { Component } from 'react';
import { View } from '@instructure/ui-view';
import { callRenderProp, passthroughProps } from '@instructure/ui-react-utils';
import { testable } from '@instructure/ui-testable';
import { withStyle } from '@instructure/emotion';
import generateStyle from './styles';
import generateComponentTheme from './theme';
import { allowedProps, propTypes } from './props';

/**
---
category: components
---
**/
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
let ProgressBar = (_dec = withStyle(generateStyle, generateComponentTheme), _dec2 = testable(), _dec(_class = _dec2(_class = (_ProgressBar = class ProgressBar extends Component {
  constructor(...args) {
    super(...args);
    this.ref = null;
    this.handleRef = el => {
      const elementRef = this.props.elementRef;
      this.ref = el;
      if (typeof elementRef === 'function') {
        elementRef(el);
      }
    };
  }
  componentDidMount() {
    var _this$props$makeStyle, _this$props;
    (_this$props$makeStyle = (_this$props = this.props).makeStyles) === null || _this$props$makeStyle === void 0 ? void 0 : _this$props$makeStyle.call(_this$props);
  }
  componentDidUpdate() {
    var _this$props$makeStyle2, _this$props2;
    (_this$props$makeStyle2 = (_this$props2 = this.props).makeStyles) === null || _this$props$makeStyle2 === void 0 ? void 0 : _this$props$makeStyle2.call(_this$props2);
  }
  render() {
    const _this$props3 = this.props,
      renderValue = _this$props3.renderValue,
      formatScreenReaderValue = _this$props3.formatScreenReaderValue,
      valueNow = _this$props3.valueNow,
      valueMax = _this$props3.valueMax,
      screenReaderLabel = _this$props3.screenReaderLabel,
      size = _this$props3.size,
      color = _this$props3.color,
      meterColor = _this$props3.meterColor,
      renderValueInside = _this$props3.renderValueInside,
      styles = _this$props3.styles,
      props = _objectWithoutProperties(_this$props3, _excluded);
    const valueText = typeof formatScreenReaderValue === 'function' ? formatScreenReaderValue({
      valueNow: valueNow,
      valueMax: valueMax
    }) : void 0;
    const value = callRenderProp(renderValue, {
      valueNow: valueNow,
      valueMax: valueMax
    });
    return _jsxs(View, {
      ...passthroughProps(props),
      as: this.props.as,
      css: styles === null || styles === void 0 ? void 0 : styles.progressBar,
      margin: this.props.margin,
      elementRef: this.handleRef,
      children: [_jsxs("span", {
        css: styles === null || styles === void 0 ? void 0 : styles.trackLayout,
        children: [_jsx("progress", {
          css: styles === null || styles === void 0 ? void 0 : styles.htmlProgress,
          max: valueMax,
          value: valueNow,
          "aria-valuetext": valueText,
          "aria-label": screenReaderLabel
        }), _jsx("span", {
          css: styles === null || styles === void 0 ? void 0 : styles.track,
          role: "presentation",
          "aria-hidden": "true",
          children: _jsx("span", {
            css: styles === null || styles === void 0 ? void 0 : styles.trackValue,
            children: renderValueInside && value
          })
        })]
      }), value && !renderValueInside && _jsx("span", {
        css: styles === null || styles === void 0 ? void 0 : styles.value,
        "aria-hidden": "true",
        children: value
      })]
    });
  }
}, _ProgressBar.displayName = "ProgressBar", _ProgressBar.componentId = 'ProgressBar', _ProgressBar.allowedProps = allowedProps, _ProgressBar.propTypes = propTypes, _ProgressBar.defaultProps = {
  formatScreenReaderValue: ({
    valueNow,
    valueMax
  }) => `${valueNow} / ${valueMax}`,
  size: 'medium',
  valueMax: 100,
  valueNow: 0,
  as: 'div',
  color: 'primary',
  shouldAnimate: false,
  // default to showing `success` color on completion
  meterColor: ({
    valueNow,
    valueMax
  }) => valueNow / valueMax >= 1 ? 'success' : 'brand'
}, _ProgressBar)) || _class) || _class);
export default ProgressBar;
export { ProgressBar };